import React from 'react';
import {
	Menu,
	MenuItem,
	Fade,
	ListSubheader,
	Checkbox,
	ListItemText,
	Typography,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { AccessClaims, Permission, ShopRole } from 'common/types';
import { getPermissionOptions, getRoleOptions, RoleOption, PermissionOption } from '../utils';
import { useTranslation } from 'services/localization/useTranslation';
import { getAllPermissions, getAllShopRoles, getShopRoles } from 'common/modules/users/utils';

interface Props {
	open: boolean;
	value: AccessClaims;
	onChange: (value: AccessClaims) => void;
	onClose: () => void;
	anchorEl?: HTMLElement | null;
}

const getUpdatedOptions = <T extends ShopRole | Permission>(option: T, options: T[]) =>
	options.includes(option) ? options.filter((r) => r !== option) : [...options, option];

const RolesPermissionsSelect = (props: Props) => {
	const { open, value, onChange, onClose, anchorEl } = props;
	const { roles: _roles, permissions } = value;
	const roles = getShopRoles(_roles);
	const classes = useStyles();
	const { t } = useTranslation();

	const roleOptions = getRoleOptions();
	const permissionOptions = getPermissionOptions();

	const isSelectedAll =
		roles.length === roleOptions.length && permissions.length === permissionOptions.length;

	const handleSelectedAll = () => {
		const updatedValue: AccessClaims = isSelectedAll
			? { roles: [], permissions: [] }
			: { roles: getAllShopRoles(), permissions: getAllPermissions() };
		onChange(updatedValue);
	};

	const handleRoleOptionClick = (role: ShopRole) => {
		const updatedValue: AccessClaims = { ...value, roles: getUpdatedOptions(role, roles) };
		onChange(updatedValue);
	};

	const handlePermissionOptionClick = (permission: Permission) => {
		const updatedValue: AccessClaims = {
			...value,
			permissions: getUpdatedOptions(permission, permissions),
		};
		onChange(updatedValue);
	};

	const renderOption = <T extends ShopRole | Permission>(
		option: RoleOption | PermissionOption,
		selectedOptions: T[],
		onClick: (value: T) => void,
	) => (
		<MenuItem key={option.value} onClick={() => onClick(option.value as T)}>
			<Checkbox
				color="primary"
				checked={selectedOptions.includes(option.value as T)}
				className={classes.checkbox}
			/>
			<ListItemText primary={option.label} />
		</MenuItem>
	);

	return (
		<Menu
			anchorEl={anchorEl}
			open={open}
			onClose={onClose}
			keepMounted
			TransitionComponent={Fade}
			classes={{
				paper: classes.menuPaper,
			}}
			anchorOrigin={{
				vertical: 'bottom',
				horizontal: 'left',
			}}
			transformOrigin={{
				vertical: 'top',
				horizontal: 'left',
			}}
		>
			<MenuItem onClick={handleSelectedAll}>
				<Checkbox color="primary" checked={isSelectedAll} className={classes.checkboxAll} />
				<ListItemText primary={t('common:count.all')} />
			</MenuItem>
			<ListSubheader className={classes.subHeader}>
				<Typography variant="caption">{t('users.roles', 'Roles')}</Typography>
			</ListSubheader>
			{roleOptions.map((role) => renderOption(role, roles, handleRoleOptionClick))}
			<ListSubheader className={classes.subHeader}>
				<Typography variant="caption">{t('users.additionalPermission')}</Typography>
			</ListSubheader>
			{permissionOptions.map((permission) =>
				renderOption(permission, permissions, handlePermissionOptionClick),
			)}
		</Menu>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		menuPaper: {
			width: 250,
		},
		subHeader: {
			textTransform: 'uppercase',
			lineHeight: '32px',
		},
		checkboxAll: {
			marginLeft: theme.spacing(-1.5),
		},
		checkbox: {
			marginLeft: theme.spacing(0.75),
		},
	}),
);

export default RolesPermissionsSelect;
